/**
 * 创建群组功能
 * @author sunzhiguang
 * @date 2020/9/22
 */
import React, { useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Employee, Group, GroupAttribute, Session, UID } from "@/types/chat";
import { employeeToUid, getInvitees, isEqualUid, isWorkSession } from "@/utils/chat/message";
import { canInviteMember } from "@/utils/chat/group";
import ImService from "@/server/ImService";
import { openUserSelectionDefaultModal } from "@/utils/modals";
import { message, Modal, Select } from "antd";
import get from "lodash/get";
import GroupRoster from "@jd/jdee.im.sdk/lib/es/model/Roster";
import {
  UserSelectionOptions,
  UserSelectionResult
} from "@jd/focus-desktop-comps/lib/es/UserSelectionModal/index";
import { InputPlacement } from "@jd/focus-desktop-comps/lib/es/UserSelectionModal/types";
import {
  checkInvitee,
  CheckInviteeResult,
  createCrossPlatformGroup,
  createExternalGroup,
  createGroupSession,
  inviteAddGroupApply,
  inviteUser,
  mapGroupRosters,
  mapSelectorResult,
  mapSessionMembers,
  mergeGroupRoster
} from "@/components/chat/GroupUserSelector/common";
import Prompt from "@/baseComponents/ModalComponent/prompt";
import IconFont from "@/components/icon";
import Context from "@/components/chat/context";
import { ContactShowType, UserSelTab } from "@jd/focus-desktop-comps/lib/es/UserSelection/types";
import "./index.less";
import { getGroupInfo, getGroupSizeLimitRule } from "@/api/egroup";
import config, { ConfigEnum } from "@/config/config";
import { debounce } from "lodash";
import Bus from "@/utils/bus";
import { ListItemData } from "@jd/focus-desktop-comps/lib/es/ListItem";
import { FocusSDK } from "@/utils";
import { isChatPage } from "@/utils/chat/index";
import GlobalContext from "@/context/GlobalContext";

// const logger = log.getLogger('comp:UserSelector');

/**
 * 人员选择器 创建群组， 群添加人员
 */
export default function useGroupUserSelector({
  selectedSession,
  currentEmployee,
  groupRosters,
  sessionMembers,
  userData,
  callback,
  customAreaExtra
}: {
  selectedSession?: any;
  currentEmployee: Employee;
  groupRosters?: Partial<GroupRoster>[];
  sessionMembers?: Partial<Employee>[];
  userData?: any;
  callback?: Function;
  forwardMessages?: Function;
  customAreaExtra?: React.ReactNode;
}) {
  const hideMessageRef = useRef<(() => void) | null>(null);
  const history = useHistory();
  const { t } = useTranslation("chat");
  const { appRuntimeEnv } = React.useContext(GlobalContext);
  const { groupRosterIdentity } = React.useContext(Context);
  const chattingRecordsRef = useRef<any[]>([]);
  const shareChattingRecordsRef = useRef<boolean>(false);
  const lockCreateGroup = useRef<boolean>(false);
  const isWorkGroup = selectedSession && isWorkSession(selectedSession);
  const reason = t("apply-to-group-reason", {
    unitName: currentEmployee?.unitName || currentEmployee?.deptName,
    name: currentEmployee?.name,
    groupName: selectedSession?.info.name
  });
  const isShow =
    currentEmployee.teamId === appRuntimeEnv.bjwpConfig.teamId
      ? false
      : config[ConfigEnum.EXTERNAL_CONTACTS_ORG];
  let inviteLock = false;
  // 添加成员  入参
  const addMemberModalOptions = useMemo((): UserSelectionOptions & {
    tabSort: { tab: UserSelTab; sort: number }[];
  } => {
    const groupRosterListItems = mapGroupRosters(groupRosters || [], currentEmployee);
    // const tabs: (keyof typeof UserSelTab)[] = !config[ConfigEnum.EXTERNAL_CONTACTS_ORG]
    //   ? ["recent", "group", "externalContacts"]
    //   : ["recent", "org", "group", "externalContacts"];
    // if (!isBjme()) {
    //     tabs.push('externalContacts');
    // }
    // const selectedMember = [...groupRosterListItems, ...strangerMember];
    return {
      title: t("add_member"),
      resultUsers: groupRosterListItems, // 选中的用户数据 （已经在群里的成员
      frozenIds: groupRosterListItems.map((item) => item.id),
      tabs: ["recent", "group"],
      tabSort: [{ tab: UserSelTab.recent, sort: 1 }],
      currentUser: employeeToUid(currentEmployee),
      contactShowType: isWorkGroup ? ContactShowType.sameTenement : ContactShowType.all,
      // contactsFilterFlag: isWorkGroup ? ["crossTeam", "crossPlatform", "sameTeam"] : [],
      canSelectAll: true,
      notLoad: true,
      customAreaExtra: customAreaExtra || undefined,
      canSelectStranger: isShow,
      // maxSelectedNum: 5,
      strangerLeaveWordInput: {
        maxLength: 100,
        placeholder: reason,
        isTextArea: true
      }
    };
  }, [groupRosters, currentEmployee, t, isWorkGroup, customAreaExtra, isShow, reason]);

  const onChattingRecordsChange = useCallback((messages) => {
    chattingRecordsRef.current = messages;
  }, []);

  const onShareChattingRecordsCheckedChange = useCallback((check) => {
    shareChattingRecordsRef.current = check;
  }, []);

  // 创建群组 入参
  const createGroupModalOptions = useMemo((): UserSelectionOptions & {
    tabSort: { tab: UserSelTab; sort: number }[];
  } => {
    const sessionMemberListItems = mapSessionMembers(sessionMembers || []);
    // const tabs: (keyof typeof UserSelTab)[] = !config[ConfigEnum.EXTERNAL_CONTACTS_ORG]
    //   ? ["recent", "group", "externalContacts"]
    //   : ["recent", "org", "workLine", "group", "externalContacts"];

    const [groupNameMaxLength, groupDescMaxLength] = [50, 500];

    // if (!isBjme()) {
    //     tabs.push('externalContacts');
    // }
    return {
      title: t("create_group"),
      modalWrapClass: "create-group-modal",
      resultUsers: sessionMemberListItems,
      extraInput: {
        maxLength: groupNameMaxLength,
        placeholder: t("please-enter-group-name"),
        isTextArea: true
      },
      despInput: {
        maxLength: groupDescMaxLength,
        placeholder: t("please-enter-group-intro"),
        isTextArea: true
      },
      // maxSelectedNum: 5,
      // limitOpts: [
      //   { limit: 100, desc: "100" },
      //   { limit: 500, desc: "500" }
      // ],
      frozenIds: sessionMemberListItems.map((item: any) => item.id),
      tabs: ["recent", "group"],
      tabSort: [{ tab: UserSelTab.recent, sort: 1 }],
      // contactShowType: ContactShowType.crossTeamAndCrossPlatform,
      // selectGroupType: SelectGroupType.innerAndCrossTenement,
      currentUser: employeeToUid(currentEmployee),
      canSelectAll: true,
      // chooseOnlyDepart: true,
      // canSelectDept: true,
      inputPlacement: InputPlacement.bodyRightBottom,
      inputWrapClass: "group-input-wrap",
      validate(data) {
        return new Promise((resolve, reject) => {
          const invitees = getInvitees(data);
          if (invitees.length < 3) {
            message.info(t("group-limit-number"));
            reject(t("group-limit-number"));
            return;
          }
          const groupName = get(data, "data.extraString", "");
          const groupDesc = get(data, "data.despInputValue", "");
          if (!groupName) {
            message.info(t("required-group-name"));
            reject(t("required-group-name"));
            return;
          }

          if (groupName.length > groupNameMaxLength) {
            message.info(
              t("content over maxLength", {
                name: "群名称",
                maxLength: groupNameMaxLength
              })
            );
            reject(
              t("content over maxLength", {
                name: "群名称",
                maxLength: groupNameMaxLength
              })
            );
            return;
          }

          if (groupDesc && groupDesc.length > groupDescMaxLength) {
            message.info(
              t("content over maxLength", {
                name: "群介绍",
                maxLength: groupDescMaxLength
              })
            );
            reject(
              t("content over maxLength", {
                name: "群介绍",
                maxLength: groupDescMaxLength
              })
            );
            return;
          }

          resolve();
        });
      }
    };
  }, [currentEmployee, sessionMembers, t]);

  const sendInviteApply = useCallback(
    async (invitees: UID[], close: () => void) => {
      if (!selectedSession) {
        return null;
      }
      const result = await inviteAddGroupApply(selectedSession.sessionId, invitees);
      if (result) {
        message.warn(t("apply.send_to_admin"));
        close?.();
      } else {
        message.error(t("add-failed"));
      }
    },
    [selectedSession, t]
  );

  const createGroupEnd = useCallback(
    (session: Session | null, close) => {
      if (session?.sessionId) {
        close?.();
        message.success(t("created-successfully"));
        const url = `/messages/g/${session?.sessionId}`;
        if (isChatPage()) {
          FocusSDK.launchNewSession(url);
        } else {
          history.push(url);
        }
      }
      // else {
      //     message.error(t('creation-failed'));
      // }
    },
    [history, t]
  );

  const props = {
    title: t("create_external_group_title"),
    content: t("create_external_group_content"),
    cancelText: t("cancelText"),
    okText: t("determine")
  };

  const openCreateCrossPlatformGroupModal_addMember = useCallback(
    (invitees: UID[], close: () => void) => {
      const onOk = async () => {
        //  合并当前群成员 和 邀请的成员
        const inviteesData = mergeGroupRoster(invitees, groupRosters || []);
        let session = await createCrossPlatformGroup({
          invitees: inviteesData,
          name: selectedSession?.info.name || ""
        });
        createGroupEnd(session, close);
      };
      const onCancel = () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        inviteLock = false;
      };
      if (chattingRecordsRef.current.length && shareChattingRecordsRef.current) {
        Modal.confirm({
          title: t("create_external_group_content"),
          content: (
            <p
              style={{
                textAlign: "center",
                fontSize: "14px",
                color: "#8F959E",
                lineHeight: "20px",
                marginTop: "8px"
              }}
            >
              {t("create_external_group_no_share_records")}
            </p>
          ),
          onOk,
          onCancel,
          cancelText: t("cancelText"),
          okText: t("determine"),
          icon: null,
          centered: true
        });
      } else {
        Prompt({
          ...props,
          onOk,
          onCancel,
          icon: <IconFont type="iconic_failure" style={{ color: "#F96137" }} />
        });
      }
    },
    [groupRosters, selectedSession?.info.name, createGroupEnd, t, props]
  );

  const openCreateExternalGroupModal_addMember = useCallback(
    (invitees: UID[], close: () => void) => {
      const onOk = async () => {
        //  合并当前群成员 和 邀请的成员
        const inviteesData = mergeGroupRoster(invitees, groupRosters || []);
        let session = await createExternalGroup({
          invitees: inviteesData,
          name: selectedSession?.info.name || ""
        });
        Bus.off("chatting-records-commit", onChattingRecordsChange);
        Bus.off("chatting-records-checkbox-change", onShareChattingRecordsCheckedChange);
        createGroupEnd(session, close);
      };
      const onCancel = () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        inviteLock = false;
      };
      if (chattingRecordsRef.current.length && shareChattingRecordsRef.current) {
        Modal.confirm({
          title: t("create_external_group_content"),
          content: (
            <p
              style={{
                textAlign: "center",
                fontSize: "14px",
                color: "#8F959E",
                lineHeight: "20px",
                marginTop: "8px"
              }}
            >
              {t("create_external_group_no_share_records")}
            </p>
          ),
          onOk,
          onCancel,
          cancelText: t("cancelText"),
          okText: t("determine"),
          icon: null,
          centered: true
        });
      } else {
        Prompt({
          ...props,
          onOk,
          onCancel,
          icon: <IconFont type="iconic_failure" style={{ color: "#F96137" }} />
        });
      }
    },
    [
      groupRosters,
      selectedSession?.info.name,
      onChattingRecordsChange,
      onShareChattingRecordsCheckedChange,
      createGroupEnd,
      t,
      props
    ]
  );
  const addMembers = useCallback(
    // eslint-disable-next-line complexity
    async (invitees: UID[], close: () => void, reason?: string) => {
      if (!selectedSession) {
        return null;
      }
      const hasChattingRecords = !!(
        chattingRecordsRef.current.length && shareChattingRecordsRef.current
      );
      const filterStranger = invitees.filter((item: any) => item.fromGlobalSearch !== "1");
      const result = await inviteUser(selectedSession.sessionId, invitees, reason, "0", {
        noEmit: hasChattingRecords,
        noSaveDB: hasChattingRecords,
        shareMsgCount:
          hasChattingRecords && filterStranger.length ? chattingRecordsRef.current.length : 0
      });
      if (result.success) {
        if (callback) {
          let groupInfo = selectedSession.info as GroupAttribute;
          callback(groupInfo.gid);
        }
        if (hasChattingRecords && filterStranger.length) {
          hideMessageRef.current = message.loading("沟通记录分享中", 0);
          const mids = chattingRecordsRef.current.map((item) => item.mid);
          const imService = ImService.getInstance();
          const inViteForwardResult = await imService.inviteUserWithMergeMessage(
            selectedSession.sessionId,
            mids,
            filterStranger
          );
          hideMessageRef.current?.();
          if (inViteForwardResult) {
            setTimeout(() => {
              message.success("分享成功", 3);
            }, 600);
          } else {
            setTimeout(() => {
              message.error("分享失败", 3);
            }, 600);
          }
          chattingRecordsRef.current = [];
          shareChattingRecordsRef.current = false;
          close?.();
        } else {
          message.success(t("invited-sended"));
          chattingRecordsRef.current = [];
          shareChattingRecordsRef.current = false;
          close?.();
        }
        // 解决添加成员后不即时更新群成员的bug---xly 考虑分享聊天记录 1114
        Bus.emit(`chat:session-member-update:${selectedSession?.sessionId}`);
      } else {
        hideMessageRef.current?.();
        // 如果返回failResult中的 code值为1103036，客户端进行notifyFlag字段的判断：
        //     notifyFlag为1，进行发送群主验证通知审核的逻辑，结束。
        //     notifyFlag为0，判断inviteMode字段：
        //        i:  inviteMode为1，提示仅群主和管理员能邀请进群，结束。
        //        ii: inviteMode为0，不做任何处理，结束。
        const { notifyFlag, inviteMode } = result.data || {};
        let code = result.data?.code;
        // 创建一个新的跨平台群
        if (code === "10246210243" || code === "5125210243") {
          openCreateCrossPlatformGroupModal_addMember(invitees, close);
          return;
        }
        // 创建一个外部群
        if (code === "5125210246") {
          openCreateExternalGroupModal_addMember(invitees, close);
          return;
        }
        if (code === 1103036) {
          if (notifyFlag === 1) {
            await sendInviteApply(invitees, close);
          } else {
            if (inviteMode === 1) {
              message.warn(t("apply.only_invite_join_group"));
            }
            close?.();
          }
        } else {
          message.error(result.data?.message || t("add-failed"));
          close?.();
        }
      }
    },
    [
      selectedSession,
      callback,
      t,
      openCreateCrossPlatformGroupModal_addMember,
      openCreateExternalGroupModal_addMember,
      sendInviteApply
    ]
  );

  const inviteMembers = useCallback(
    async (invitees: UID[], close: () => void, reason?: string) => {
      if (!selectedSession) {
        return null;
      }
      if (canInviteMember(selectedSession.info as Group, groupRosterIdentity)) {
        // 直接邀请入群
        await addMembers(invitees, close, reason);
      } else {
        // 发送邀请申请
        await sendInviteApply(invitees, close);
      }
    },
    [selectedSession, groupRosterIdentity, addMembers, sendInviteApply]
  );

  const addMemberModalCallback = debounce(
    useCallback(
      async (data: UserSelectionResult, close: () => void) => {
        if (!selectedSession) {
          return null;
        }
        if (inviteLock) {
          return null;
        }
        // if (forwardMessages) {
        //     forwardMessages();
        // }
        const invitees = mapSelectorResult(data);
        if (invitees.length === 0) {
          Bus.off("chatting-records-commit", onChattingRecordsChange);
          Bus.off("chatting-records-checkbox-change", onShareChattingRecordsCheckedChange);
          close();
          return false;
        }
        const onCancel = () => {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          inviteLock = false;
        };
        const onOk = async () => {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          inviteLock = true;
          await inviteMembers(invitees, close, data?.data?.strangerLeaveWordInputValue || reason);
        };
        const strangerResult = get(data, "data.strangerResult", []) as ListItemData[];
        if (
          chattingRecordsRef.current.length &&
          shareChattingRecordsRef.current &&
          strangerResult &&
          strangerResult.length !== 0
        ) {
          Modal.confirm({
            title: t("not_share_records_to_stranger_title"),
            content: (
              <p
                style={{
                  textAlign: "center",
                  fontSize: "14px",
                  color: "#8F959E",
                  lineHeight: "20px",
                  marginTop: "8px"
                }}
              >
                {t("not_share_records_to_stranger_tip")}
              </p>
            ),
            onOk,
            onCancel,
            cancelText: t("cancelText"),
            okText: t("determine"),
            icon: null,
            centered: true
          });
        } else {
          onOk();
        }
      },
      [
        selectedSession,
        t,
        onChattingRecordsChange,
        onShareChattingRecordsCheckedChange,
        inviteMembers
      ]
    ),
    400
  );

  // const openCreateExternalGroupModal_creatGroup = useCallback(
  //   (invitees: UID[], groupName: string, groupDesp: string, close: () => void) => {
  //     const onOk = async () => {
  //       //  合并当前群成员 和 邀请的成员
  //       const inviteesData = mergeGroupRoster(invitees, groupRosters || []);
  //       let session = await createExternalGroup(inviteesData, selectedSession?.info.name || "");
  //       createGroupEnd(session, close);
  //     };
  //     Prompt({
  //       ...props,
  //       onOk,
  //       icon: <IconFont type="iconic_failure" style={{ color: "#F96137" }} />
  //     });
  //   },
  //   [props, groupRosters, selectedSession?.info.name, createGroupEnd]
  // );

  // 创建群组 回调
  const createGroupModalCallBack = debounce(
    useCallback(
      // eslint-disable-next-line max-params
      async (data, close, _, cancelLoading) => {
        const invitees = getInvitees(data);
        const groupName = get(data, "data.extraString", "");
        const groupLimit = get(data, "data.selectedLimit", null);
        const groupDesp = get(data, "data.despInputValue", "");
        const result = checkInvitee(invitees, currentEmployee);
        console.log("groupLimit:", groupLimit);
        const onCancel = () => {
          lockCreateGroup.current = false;
          cancelLoading?.();
        };

        // 加锁
        if (lockCreateGroup.current) {
          return false;
        }
        lockCreateGroup.current = true;
        // const stt = setTimeout(() => {
        //   clearTimeout(stt);
        //   lockCreateGroup = false;
        // }, 1500);

        let session = null;
        if (result === CheckInviteeResult.Normal) {
          session = await createGroupSession({
            invitees,
            name: groupName,
            intro: groupDesp,
            max: groupLimit?.limit
          });
          createGroupEnd(session, close);
        } else if (result === CheckInviteeResult.External) {
          const onOk = async () => {
            session = await createExternalGroup({
              invitees,
              name: groupName,
              intro: groupDesp,
              max: groupLimit?.limit
            });
            createGroupEnd(session, close);
          };
          Prompt({
            ...props,
            onOk,
            onCancel,
            icon: <IconFont type="iconic_failure" style={{ color: "#F96137" }} />
          });
        } else if (result === CheckInviteeResult.CrossPlatform) {
          const onOk = async () => {
            session = await createCrossPlatformGroup({
              invitees,
              name: groupName,
              intro: groupDesp,
              max: groupLimit?.limit
            });
            createGroupEnd(session, close);
          };
          Prompt({
            ...props,
            onOk,
            onCancel,
            icon: <IconFont type="iconic_failure" style={{ color: "#F96137" }} />
          });
        }
        // createGroupEnd(session, close);
      },
      [currentEmployee, createGroupEnd, props]
    ),
    400
  );

  const addMemberModalCallbackClose = useCallback(() => {
    hideMessageRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    hideMessageRef.current = null;
    chattingRecordsRef.current = [];
    shareChattingRecordsRef.current = false;
    Bus.off("chatting-records-commit", onChattingRecordsChange);
    Bus.off("chatting-records-checkbox-change", onShareChattingRecordsCheckedChange);
  }, [onChattingRecordsChange, onShareChattingRecordsCheckedChange]);

  const openAddMemberModal = useCallback(async () => {
    if (!selectedSession) {
      return;
    }
    console.log("openAddMemberModal selectedSession:", selectedSession);
    const result = await getGroupInfo({
      groupIds: [{ groupId: selectedSession?.gid || selectedSession?.id }]
    });
    const groupInfo = result?.groups?.[0] || {};
    const { max = undefined } = groupInfo;
    Bus.on("chatting-records-commit", onChattingRecordsChange);
    Bus.on("chatting-records-checkbox-change", onShareChattingRecordsCheckedChange);
    openUserSelectionDefaultModal(
      {
        ...addMemberModalOptions,
        maxSelectedNum: max
      },
      addMemberModalCallback,
      userData,
      addMemberModalCallbackClose
    );
  }, [selectedSession, addMemberModalOptions, addMemberModalCallback, userData]); // eslint-disable-line

  const openCreateGroupModal = useCallback(async () => {
    console.log("userData:", userData);
    const { ddAppId = "", userId = "", teamUserInfo = {} } = userData;
    const { teamId = "" } = teamUserInfo;
    const limitRule: any =
      (await getGroupSizeLimitRule({ app: ddAppId, pin: userId, teamId })) || {};
    const { ruleGradient = [] } = limitRule;
    console.log("ruleGradient", ruleGradient);
    let defaultLimitOpt = ruleGradient[0];
    let localLimitOpt = JSON.parse(
      localStorage.getItem(`groupLimitOpt:${teamId}:${userId}`) || "{}"
    );
    if (
      localLimitOpt.limit &&
      localLimitOpt.limit <= ruleGradient[ruleGradient.length - 1]?.limit
    ) {
      defaultLimitOpt = localLimitOpt;
    }
    lockCreateGroup.current = false;
    openUserSelectionDefaultModal(
      {
        ...createGroupModalOptions,
        defaultLimitOpt: defaultLimitOpt,
        rightHeaderExtra: (data: any, eventBus: any, extraOptions:any) => {
          console.log("rightHeaderExtra extraOptions:", extraOptions);
          const optList = ruleGradient;
          return (
            <div>
              <span>群人数上限&nbsp;</span>
              <Select
                value={extraOptions?.selectedLimit?.limit}
                onChange={(val) => {
                  let opt = optList.find((item: any) => item.limit === val);
                  eventBus.emit("setSelectedLimit", {
                    opt,
                    cb: (cbData: any) => {
                      console.log("cbData:", cbData);
                      const { status, selected } = cbData;
                      if (status === "success" && selected) {
                        localStorage.setItem(
                          `groupLimitOpt:${teamId}:${userId}`,
                          JSON.stringify(selected)
                        );
                      }
                    }
                  });
                }}
              >
                {optList.map((opt: any, idx: number) => (
                  <Select.Option key={idx} value={opt.limit}>
                    {opt.desc}
                  </Select.Option>
                ))}
              </Select>
            </div>
          );
        }
      },
      createGroupModalCallBack,
      userData
    );
  }, [createGroupModalOptions, createGroupModalCallBack, userData]); // eslint-disable-line

  return () => {
    if (!selectedSession) {
      openCreateGroupModal();
    } else if (selectedSession.isGroup) {
      // getStrangerList();
      openAddMemberModal();
    } else if (selectedSession.isSingle && !selectedSession.isNotice) {
      openCreateGroupModal();
    }
  };
}
